export type UiLocale = "en" | "es" | "pt";

export interface JobLanguageSource {
  TH_Job_Language__c?: unknown;
  Interview_Language__c?: unknown;
  interview_language?: unknown;
}

export interface ResolvedInterviewLanguage {
  uiLocale: UiLocale;
  interviewLanguage: string;
  recognitionLocale: string;
  htmlLang: string;
}

const HTML_LANG: Record<UiLocale, string> = {
  en: "en",
  es: "es-419",
  pt: "pt-BR",
};

const fold = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/_/g, "-");

const tokensOf = (value: string): string[] => fold(value).match(/[a-z0-9]+/g) || [];

const CODE_FAMILIES: Record<string, UiLocale> = {
  en: "en",
  es: "es",
  pt: "pt",
};

const MARKERS: Array<[UiLocale, string[]]> = [
  ["es", ["spanish", "espanol", "castellano"]],
  ["pt", ["portuguese", "portugues"]],
  ["en", ["english", "ingles"]],
];

export const resolveUiLocale = (language?: string | null): UiLocale => {
  const folded = fold(language || "");
  if (!folded) {
    return "en";
  }
  const primary = folded.split("[")[0].split("(")[0].trim();
  const tokens = tokensOf(primary);
  for (const [locale, markers] of MARKERS) {
    if (markers.some((marker) => tokens.includes(marker))) {
      return locale;
    }
  }
  const code = tokens[0];
  if (code && CODE_FAMILIES[code] && tokens.every((token) => token.length <= 3 || /^\d+$/.test(token))) {
    return CODE_FAMILIES[code];
  }
  return "en";
};

const parsePolicy = (
  raw: unknown
): { primary: string; primaryLabel: string } | null => {
  if (raw == null) {
    return null;
  }
  let loaded: unknown = raw;
  if (typeof raw === "string") {
    const stripped = raw.trim();
    if (!stripped) {
      return null;
    }
    if (!stripped.startsWith("{") && !stripped.startsWith("[")) {
      return resolveUiLocale(stripped) === "en" && !/english|ingles/i.test(fold(stripped))
        ? null
        : { primary: stripped, primaryLabel: stripped };
    }
    try {
      loaded = JSON.parse(stripped);
    } catch {
      return null;
    }
  }
  if (typeof loaded === "string") {
    return { primary: loaded, primaryLabel: loaded };
  }
  if (!loaded || typeof loaded !== "object" || Array.isArray(loaded)) {
    return null;
  }
  const record = loaded as { primary?: unknown; primary_label?: unknown };
  const primary = String(record.primary || "").trim();
  if (!primary) {
    return null;
  }
  const primaryLabel = String(record.primary_label || primary).trim();
  return { primary, primaryLabel };
};

const looksLikeLanguage = (value: string): boolean => {
  const locale = resolveUiLocale(value);
  if (locale !== "en") {
    return true;
  }
  const tokens = tokensOf(value);
  return tokens.includes("english") || tokens.includes("ingles") || tokens[0] === "en";
};

const recognitionLocaleFor = (primary: string, locale: UiLocale): string => {
  const tag = primary.trim();
  if (/^[a-z]{2,3}(?:-[a-z0-9]{2,8})+$/i.test(tag)) {
    if (locale === "es" && /^es-es$/i.test(tag)) {
      return "es-419";
    }
    return tag;
  }
  return HTML_LANG[locale];
};

export const resolveInterviewLanguage = (
  source?: JobLanguageSource | null
): ResolvedInterviewLanguage => {
  const policy = parsePolicy(source?.TH_Job_Language__c);
  const policyLabel = policy?.primaryLabel || policy?.primary || "";
  const apiLanguage = String(source?.interview_language || "").trim();
  const legacy = String(source?.Interview_Language__c || "").trim();
  const label = [policyLabel, apiLanguage, legacy].find((value) => looksLikeLanguage(value)) || "English";
  const primary = (policy && looksLikeLanguage(policyLabel) ? policy.primary : "") || label;
  const uiLocale = resolveUiLocale(label);
  return {
    uiLocale,
    interviewLanguage: label,
    recognitionLocale: recognitionLocaleFor(primary, uiLocale),
    htmlLang: HTML_LANG[uiLocale],
  };
};

export const applyDocumentLanguage = (htmlLang: string): void => {
  if (typeof document !== "undefined" && htmlLang) {
    document.documentElement.lang = htmlLang;
  }
};
